import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { Alert } from "../../components/base/Alert";
import { Button } from "../../components/base/Button";
import { Select } from "../../components/base/Select";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { AffectedPartsPanel } from "../../components/PinnacleMapping/AffectedPartsPanel";
import { ItemTypeList } from "../../components/PinnacleMapping/ItemTypeList";
import { MappingEditor } from "../../components/PinnacleMapping/MappingEditor";
import { SetupTools } from "../../components/PinnacleMapping/SetupTools";
import { SuggestionList } from "../../components/PinnacleMapping/SuggestionList";
import { UserGuide } from "../../components/PinnacleMapping/UserGuide";
import { ItemTypeFilter, filterItemTypes, sortItemTypes } from "../../components/PinnacleMapping/pinnacleMeta";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCategories from "../../data/Category/useCategories";
import { useImportSyncCustomers } from "../../data/ImportSync/useImportSync";
import { usePinnacleItemTypes, usePinnacleUsage } from "../../data/PinnacleMapping/usePinnacleMapping";
import usePinnacleMappingMutations from "../../data/PinnacleMapping/usePinnacleMappingMutations";

const LAST_CUSTOMER_KEY = "pinnacle-mapping:customer";

function readLastCustomer() {
  try {
    return localStorage.getItem(LAST_CUSTOMER_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function rememberCustomer(id: string) {
  try {
    localStorage.setItem(LAST_CUSTOMER_KEY, id);
  } catch {
    // Storage blocked: the URL still carries the choice.
  }
}

/**
 * Maps Pinnacle item types to our part categories by hand. The mappings are global and reach every
 * customer through the category sync; choosing a customer shows their imported parts, so a mapping
 * can be checked against real stock and the affected parts moved.
 */
export default function PinnacleMappingPage() {
  const { setInfo } = usePageNameContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<ItemTypeFilter>("todo");
  const [search, setSearch] = useState("");
  const [confirmPush, setConfirmPush] = useState(false);

  const itemTypes = usePinnacleItemTypes();
  const customers = useImportSyncCustomers();
  const { categories } = useCategories({ itemsPerPage: 5000 });
  const { pushToCustomers } = usePinnacleMappingMutations();

  useEffect(() => {
    setInfo({
      name: "Pinnacle Mapping",
      desc: "Map Pinnacle item types to part categories, then move the parts already imported",
      pageTitle: "Pinnacle mapping",
    });
  }, []);

  const customerId = useMemo(() => {
    const list = customers.data ?? [];
    const requested = searchParams.get("customer") ?? readLastCustomer();
    if (requested && list.some((c) => c.id === requested)) return requested;
    return list.length === 1 ? list[0].id : undefined;
  }, [customers.data, searchParams]);

  const usage = usePinnacleUsage(customerId);

  const rows = useMemo(() => {
    const byKey = new Map((usage.data?.usage ?? []).map((u) => [u.key, u]));
    return sortItemTypes((itemTypes.data ?? []).map((itemType) => ({ itemType, usage: byKey.get(itemType.key) })));
  }, [itemTypes.data, usage.data]);

  const visible = useMemo(() => filterItemTypes(rows, filter, search), [rows, filter, search]);

  const selectedId = searchParams.get("item") ?? visible[0]?.itemType.id;
  const selected = rows.find((r) => r.itemType.id === selectedId)?.itemType;

  const categoryOptions = useMemo(
    () =>
      (categories.data ?? [])
        .map((c) => ({ id: c.id as string, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories.data],
  );

  const setParam = (key: string, value?: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const selectCustomer = (id: string) => {
    rememberCustomer(id);
    setParam("customer", id);
  };

  const customerName = customers.data?.find((c) => c.id === customerId)?.name;

  return (
    <div className="mt-4 space-y-4">
      <UserGuide />

      <div className="flex flex-wrap items-end justify-between gap-4">
        <Select
          label="Show parts from"
          value={customerId ?? ""}
          onChange={(e) => selectCustomer((e.target as HTMLSelectElement).value)}
          disabled={customers.isLoading}
          containerClassName="w-full sm:w-80"
          fullWidth
        >
          <option value="">{customers.isLoading ? "Loading customers…" : "No customer: mappings only"}</option>
          {customers.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>

        <div className="flex flex-wrap items-center gap-2">
          {confirmPush ? (
            <>
              <span className="text-sm text-gray-700">Send the confirmed mappings to every customer now?</span>
              <Button
                size="sm"
                onClick={() => pushToCustomers.mutate([], { onSettled: () => setConfirmPush(false) })}
                disabled={pushToCustomers.isPending}
              >
                Push to all
              </Button>
              <Button variant="outlined" size="sm" onClick={() => setConfirmPush(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="outlined" onClick={() => setConfirmPush(true)}>
              Push mappings to customers
            </Button>
          )}
        </div>
      </div>

      <p className="text-sm text-gray-500">
        Mappings apply to every customer and go out with the nightly category sync. Imports only use mappings someone has confirmed;
        a part no mapping fits is left uncategorised and counted here.
      </p>

      {usage.isError && <Alert severity="warn" title={`Could not load ${customerName ?? "this customer"}'s parts`} message={usage.error.message} />}
      {itemTypes.isError && <Alert severity="error" message={itemTypes.error.message} />}

      <SetupTools />

      {itemTypes.isLoading ? (
        <LoadingWheel />
      ) : rows.length === 0 ? (
        <Alert
          severity="info"
          title="No Pinnacle item types yet"
          message="Import the legacy names or the match sheet under Set up and import, or choose a customer to pick up the item types in their imported parts."
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-[18rem_1fr]">
            <aside className="border-b border-gray-200 md:border-b-0 md:border-r">
              <ItemTypeList
                rows={visible}
                selectedId={selected?.id}
                onSelect={(id) => setParam("item", id)}
                filter={filter}
                onFilterChange={setFilter}
                search={search}
                onSearchChange={setSearch}
                showUsage={!!customerId}
              />
            </aside>

            <div className="min-w-0 space-y-5 p-4">
              {selected ? (
                <>
                  <MappingEditor itemType={selected} categories={categoryOptions} />
                  <SuggestionList itemTypeId={selected.id} />
                </>
              ) : (
                <p className="text-sm text-gray-500">Choose an item type to map it.</p>
              )}
            </div>
          </div>

          {selected && customerId && (
            <div className="border-t border-gray-200 p-4">
              <AffectedPartsPanel customerId={customerId} customerName={customerName} itemTypeId={selected.id} categories={categoryOptions} />
            </div>
          )}

          {selected && !customerId && (
            <p className="border-t border-gray-200 p-4 text-sm text-gray-500">Choose a customer above to see and move their parts of this item type.</p>
          )}
        </div>
      )}
    </div>
  );
}
